<template>
  <div>
    <slot :activator="openModal"/>

    <modal
      title="Purchase Legal Career Compass"
      :show="showModal"
      scrollable
      @close="closeModal"
    >
      <l-form class="grid gap-4" @submit="submit">
        <alert
          v-if="errors.length"
          variant="error"
        >
          {{ errors }}
        </alert>

        <div ref="address"/>
        <div ref="payment"/>

        <l-button
          class="btn-lg w-full"
          :disabled="loading"
          :loading="submitting"
          type="submit"
          variant="primary"
        >
          Pay ${{ price }} (one-time fee) + tax
        </l-button>

        <p class="p-3 border rounded text-sm text-gray-500">
          We use <a class="text-gray-500" href="https://stripe.com">Stripe</a> to securely process credit card transactions over an HTTPS connection.
          We do not store your credit card on our servers.
        </p>
      </l-form>
    </modal>
  </div>
</template>

<script setup>
import Alert from "@/components/Alert.vue"
import LButton from "@/components/buttons/LButton.vue"
import LForm from "@/components/forms/LForm.vue"
import Modal from "@/components/modals/Modal.vue"
import route from "ziggy-js"
import { api } from "@/api"
import { computed, nextTick, ref } from "vue"
import { config } from "@/support"
import { useAuth } from "@/composables/useAuth"

const props = defineProps({
  autoopen: { type: Boolean },
  coupon: { type: String },
  price: { type: String, required: true },
})

const { user } = useAuth()

const errors = ref("")
const address = ref(null)
const addressReady = ref(false)
const payment = ref(null)
const paymentReady = ref(false)
const intent = ref(null)
const showModal = ref(false)
const submitting = ref(false)

const stripe = Stripe(config("services.stripe.key"))
const elements = stripe.elements({
  mode: "setup",
  currency: "usd",
})

const addressElement = elements.create("address", {
  mode: "billing",
  defaultValues: {
    name: user.value.name,
  },
}).on("ready", () => {
  addressReady.value = true
})

const paymentElement = elements.create("payment", {
  terms: {
    card: "never",
  },
}).on("ready", () => {
  paymentReady.value = true
})

const loading = computed(() => !addressReady.value || !paymentReady.value || !intent.value)

const next = route("compass.index")

const closeModal = () => {
  showModal.value = false
}

const openModal = () => {
  showModal.value = true

  api.compassSetup().then(({ data }) => {
    intent.value = data.intent
  }).catch(() => {
    errors.value = "There was a problem connecting to our credit card processor. Please reload the page."
  })

  nextTick(() => {
    paymentElement.mount(payment.value)
    paymentElement.addEventListener("change", (event) => {
      if (event.error) {
        errors.value = event.error.message
      } else {
        errors.value = ""
      }
    })

    addressElement.mount(address.value)
  })
}

const submit = async () => {
  const { error } = await elements.submit()

  if (error) {
    errors.value = error

    return
  }

  submitting.value = true

  stripe.confirmSetup({
    elements,
    clientSecret: intent.value.client_secret,
    confirmParams: {
      return_url: next,
    },
    redirect: "if_required",
  }).then(result => {
    if (result.error) {
      errors.value = result.error.message

      return
    }

    const { payment_method } = result.setupIntent

    api.compassPay({
      coupon: props.coupon,
      paymentMethod: payment_method,
    }).then(() => {
      window.location = next
    }).catch(e => {
      errors.value = e.response.data.error
      submitting.value = false
    })
  })
}

nextTick(() => {
  if (props.autoopen) {
    openModal()
  }
})
</script>
